import { Component, EventEmitter, OnDestroy, Output } from '@angular/core'

import { Store } from '@ngrx/store'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { Subject } from 'rxjs'
import { take, takeUntil, switchMap } from 'rxjs/operators'
import {
    selectCaseId,
    selectCaseMarkers,
    selectCasePlottingResults,
    selectCaseSegObjectPropertyMap,
    selectCaseSegObjectPropertyMaps,
} from 'src/app/state/case/case.selectors'
import { selectImageLayers } from 'src/app/state/viewer/viewer.selectors'
import { JobApiService } from 'src/app/shared/services/job-api.service'
import {
    ClusteringNormalization,
    PipelineType,
    PlottingParameters,
} from 'src/app/shared/model/jobs'
import { closeJobSelectionDialog } from 'src/app/shared/utils/job-selection-dialog-utils'
import { CreateGroupDialogComponent } from './create-group-dialog/create-group-dialog.component'
import { Dialog } from '@angular/cdk/dialog'
import { ToasterService } from 'src/app/shared/services/toaster.service'
import { HttpErrorResponse } from '@angular/common/http'
import { SegObjectPropertyMap } from 'src/app/state/case/case.state'
import { GenericConfirmationDialogModel } from 'src/app/shared/components/generic-confirmation-dialog/generic-confirmation-dialog.model'
import { GenericConfirmationDialogComponent } from 'src/app/shared/components/generic-confirmation-dialog/generic-confirmation-dialog.component'
import { DatasetApiService } from 'src/app/shared/services/dataset-api.service'

interface PlotDetail {
    displayedName?: string
    name: string
    option: string[]
    transformValue?: { [key: string]: string | boolean }
}

interface Plot {
    type: string
    details: PlotDetail[]
}

@Component({
    selector: 'app-plotting-panel',
    templateUrl: './plotting-panel.component.html',
    styleUrls: ['./plotting-panel.component.scss'],
})
export class PlottingPanelComponent implements OnDestroy {
    ClusteringNormalizationValues = Object.values(ClusteringNormalization)
    plottingResults: { name: string; image: string; groups: any[]; plot_details: any[] }[] = []
    plottingForm: FormGroup

    createJobMode = true
    groups: any[] = []
    readonly caseMarkers$
    readonly plottingResults$
    properties: string[] = []
    readonly SELECTED_PLOT_TYPE_KEY = 'type'
    readonly NEW_EXPERIMENT_NAME_KEY = 'name'
    readonly SELECTED_PROPERTY_KEY = 'property'
    readonly plots: Plot[] = [
        {
            type: 'Swarm plot',
            details: [
                {
                    displayedName: 'Y-axis scale',
                    name: 'log_scale',
                    option: ['log', 'linear'],
                },
            ],
        },
        {
            type: 'Box plot',
            details: [
                {
                    displayedName: 'Y-axis scale',
                    name: 'log_scale',
                    option: ['log', 'linear'],
                },
            ],
        },
        {
            type: 'Violin plot',
            details: [
                {
                    displayedName: 'Y-axis scale',
                    name: 'log_scale',
                    option: ['log', 'linear'],
                },
                {
                    displayedName: 'Density normalization',
                    name: 'density_norm',
                    option: ['area', 'count', 'width'],
                },
                {
                    displayedName: 'Violin interior representation',
                    name: 'inner',
                    option: ['box', 'quartile', 'point'],
                },
            ],
        },
        {
            type: 'Bar plot',
            details: [
                {
                    displayedName: 'Y-axis scale',
                    name: 'log_scale',
                    option: ['log', 'linear'],
                },
                {
                    displayedName: 'Errorbar method',
                    name: 'errorbar',
                    option: [
                        'confidence interval',
                        'percentile interval',
                        'standard error',
                        'standard deviation',
                    ],
                    transformValue: {
                        'confidence interval': 'ci',
                        'percentile interval': 'pi',
                        'standard error': 'se',
                        'standard deviation': 'sd',
                    },
                },
            ],
        },
        {
            type: 'Histogram',
            details: [
                {
                    displayedName: 'Y-axis scale',
                    name: 'log_scale',
                    option: ['log', 'linear'],
                },
                {
                    displayedName: 'Show KDE',
                    name: 'show_kde',
                    option: ['true', 'false'],
                    transformValue: {
                        true: true,
                        false: false,
                    },
                },
                {
                    displayedName: 'Stack',
                    name: 'stack',
                    option: ['vertical', 'horizontal', 'none'],
                },
                {
                    displayedName: 'Bin statistics',
                    name: 'bin_statistics',
                    option: ['count', 'frequency', 'probability', 'percent', 'density'],
                },
            ],
        },
        {
            type: 'CDF',
            details: [
                {
                    displayedName: 'Y-axis scale',
                    name: 'log_scale',
                    option: ['log', 'linear'],
                },
                {
                    displayedName: 'Distribution statistics',
                    name: 'dist_statistics',
                    option: ['proportion', 'percent', 'count'],
                },
            ],
        },
        {
            type: 'Heatmap',
            details: [
                {
                    displayedName: 'Color map',
                    name: 'cmap',
                    option: ['gray', 'Reds', 'Blues', 'cividis'],
                },
            ],
        },
    ]

    private readonly destroy$ = new Subject<void>()
    enableJobButton = false
    // private readonly wikiUrl = environment.wikiUrl
    readonly segObjectPropertyMap$
    readonly segObjectPropertyMaps$
    readonly sourceLayers$ = this.store.select(selectImageLayers)
    cohorts: { id: number; name: string }[] = []
    selections: { name: string; type: number }[] = []
    segObjectPropertyMap: SegObjectPropertyMap | undefined

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly store: Store,
        private readonly jobApi: JobApiService,
        private readonly toaster: ToasterService,
        private readonly dialog: Dialog,
        private readonly datasetApi: DatasetApiService
    ) {
        this.segObjectPropertyMap$ = this.store.select(selectCaseSegObjectPropertyMap)
        this.segObjectPropertyMaps$ = this.store.select(selectCaseSegObjectPropertyMaps)
        this.plottingResults$ = this.store.select(selectCasePlottingResults)
        this.plottingResults$.subscribe(results => {
            this.plottingResults = results
        })
        this.caseMarkers$ = this.store.select(selectCaseMarkers)
        this.segObjectPropertyMap$.pipe().subscribe(map => {
            this.segObjectPropertyMap = map
        })

        this.caseMarkers$.pipe().subscribe(markers => {
            this.properties = markers
                .filter(m => m.segobjectpropertymap.name === this.segObjectPropertyMap?.name)
                .map(m => m.name)
            this.initiateForm()
        })

        this.initiateForm()
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    @Output() paramsSet = new EventEmitter<PlottingParameters>()

    startJob(): void {
        const formValue = { ...this.plottingForm.value }

        formValue.log_scale = formValue.log_scale === 'log'

        if (formValue.type === 'Violin plot' && formValue.inner === 'quartile') {
            formValue.inner = 'quart'
        }

        if (formValue.type === 'Bar plot' && formValue.errorbar) {
            const errorbarMap: { [key: string]: string } = {
                'confidence interval': 'ci',
                'percentile interval': 'pi',
                'standard error': 'se',
                'standard deviation': 'sd',
            }
            formValue.errorbar = errorbarMap[formValue.errorbar]
        }

        if (formValue.type === 'Histogram' && formValue.show_kde) {
            formValue.kde = formValue.show_kde === 'true'
            delete formValue.show_kde
        }

        if (formValue.type === 'Histogram' && formValue.bin_statistics) {
            formValue.stat = formValue.bin_statistics
            delete formValue.bin_statistics
        }
        if (formValue.type === 'CDF' && formValue.dist_statistics) {
            formValue.stat = formValue.dist_statistics
            delete formValue.dist_statistics
        }

        this.jobApi
            .startPlottingJob({
                name: formValue.name,
                type: PipelineType.Plotting,
                plotType: formValue.type,
                groups: this.groups,
                cohorts: this.cohorts,
                properties: formValue.property,
                details: [formValue],
                segSource: this.segObjectPropertyMap?.name!,
            })
            .subscribe({
                next: () => {
                    closeJobSelectionDialog()
                    this.initiateForm()
                },
                error: (err: HttpErrorResponse) => {
                    this.toaster.httpError(err, 'Error starting job')
                },
            })
    }

    private initiateForm(): void {
        this.segObjectPropertyMaps$.pipe(take(1)).subscribe(_ => {
            this.buildForm()

            this.plottingForm.valueChanges.subscribe(formValue => {
                const hasExperimentName = !!formValue[this.NEW_EXPERIMENT_NAME_KEY]
                const hasSelectedPlotType = !!formValue[this.SELECTED_PLOT_TYPE_KEY]
                const hasSelectedProperty = !!formValue[this.SELECTED_PROPERTY_KEY]
                const hasAtLeastOneGroup = this.groups.length > 0

                this.enableJobButton =
                    hasExperimentName &&
                    hasSelectedPlotType &&
                    hasSelectedProperty &&
                    hasAtLeastOneGroup
            })
        })
    }

    buildForm() {
        const formGroup: any = {
            [this.SELECTED_PLOT_TYPE_KEY]: new FormControl(this.plots[0].type, [
                Validators.required,
            ]),
            [this.NEW_EXPERIMENT_NAME_KEY]: new FormControl('', [Validators.required]),
            [this.SELECTED_PROPERTY_KEY]: new FormControl(this.properties[0] || '', [
                Validators.required,
            ]),
        }

        const selectedPlot = this.plots.find(p => p.type === this.plots[0].type)
        if (selectedPlot) {
            selectedPlot.details.forEach(detail => {
                formGroup[detail.name] = new FormControl(detail.option ? detail.option[0] : false)
            })
        }

        this.plottingForm = this.formBuilder.group(formGroup)

        this.plottingForm
            .get(this.SELECTED_PLOT_TYPE_KEY)
            ?.valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe(plotType => {
                this.updateFormControls(plotType)
            })
    }

    private updateFormControls(plotType: string): void {
        const plot = this.plots.find(p => p.type === plotType)
        if (!plot) return

        const existingControls = Object.keys(this.plottingForm.controls)
        existingControls.forEach(controlName => {
            if (
                controlName !== this.SELECTED_PLOT_TYPE_KEY &&
                controlName !== this.NEW_EXPERIMENT_NAME_KEY &&
                controlName !== this.SELECTED_PROPERTY_KEY
            ) {
                this.plottingForm.removeControl(controlName)
            }
        })

        plot.details.forEach(detail => {
            this.plottingForm.addControl(
                detail.name,
                new FormControl(detail.option ? detail.option[0] : false)
            )
        })
    }

    openGroupSelectionDialog(): void {
        if (this.segObjectPropertyMap) {
            const dialogRef = this.dialog.open<string>(CreateGroupDialogComponent, {
                autoFocus: 'first-tabbable',
            })

            dialogRef.closed.pipe(takeUntil(this.destroy$)).subscribe((group: any) => {
                if (group) {
                    this.groups = [...this.groups, group]
                }
                const formValue = this.plottingForm.value
                const hasExperimentName = !!formValue[this.NEW_EXPERIMENT_NAME_KEY]
                const hasSelectedPlotType = !!formValue[this.SELECTED_PLOT_TYPE_KEY]
                this.enableJobButton =
                    hasExperimentName && hasSelectedPlotType && this.groups.length > 0
            })
        } else {
            this.toaster.error('Select an intensity map', 'Error')
        }
    }

    getSelectedPlotDetails() {
        const selectedPlotType = this.plottingForm?.get(this.SELECTED_PLOT_TYPE_KEY)?.value
        const selectedPlot = this.plots.find(p => p.type === selectedPlotType)
        return selectedPlot?.details || []
    }

    getNodeIds(nodes: any[]): string {
        return nodes?.map(node => node.id).join(', ') || ''
    }

    removeGroup(group: any): void {
        if (this.groups.length === 1) {
            this.enableJobButton = false
        }
        this.groups = this.groups.filter(g => g !== group)
    }

    getObjectKeys(obj: any): string[] {
        if (!obj) return []
        return Object.keys(obj).filter(key => key !== 'name' && key !== 'type')
    }

    openInNewTab(url: string): void {
        window.open(url, '_blank')
    }

    deleteResult(name: string): void {
        const data: GenericConfirmationDialogModel = {
            title: 'Are you sure you want to delete this result?',
            item: name,
            confirmButtonLabel: 'Delete',
            cancelButtonLabel: 'Cancel',
        }

        this.dialog
            .open<string>(GenericConfirmationDialogComponent, {
                data,
                autoFocus: 'dialog',
            })
            .closed.pipe(
                takeUntil(this.destroy$),
                switchMap(() => this.store.select(selectCaseId).pipe(take(1))),
                switchMap(caseId => this.datasetApi.deletePlottingResult(caseId, name)),
                switchMap(() => this.store.select(selectCasePlottingResults))
            )
            .subscribe({
                error: error => {
                    this.toaster.httpError(error, 'Failed to delete plotting result')
                },
            })

        this.plottingResults = this.plottingResults.filter(result => result.name !== name)
    }
}
